import { useState, useEffect, useContext, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  TextField,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
// components
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

import { EntityContext } from '../entity';

import {SignerCard} from '../components/signer-input'

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', alignRight: false },
  { id: 'company', label: 'Email', alignRight: false },
  { id: 'role', label: 'Numero de documento', alignRight: false },
  { id: 'isVerified', label: 'Data de criação', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => (_user.signer_name ?? '').toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function SignersPage() {

  const [userList, setUserList] = useState([]);

  const [signerEmail, setSignerEmail] = useState('')
  const [signerDocumentNumber, setSignerDocumentNumber] = useState('')
  const [signerDocumentNumberError, setSignerDocumentNumberError] = useState(false);
  const [signerName, setSignerName] = useState('')
  const [signerPhone, setSignerPhone] = useState('')
  const [signerPhoneError, setSignerPhoneError] = useState('')

  const [showAddSigner, setShowAddSigner] = useState(false);

  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [signerToDelete, setSignerToDelete] = useState(null)

  const { getSigners, createSigner, DeleteSigner, signatureMethods } = useContext(EntityContext)

  const navigate = useNavigate()

  // celular só com o backend da US-008 (signature_methods no GET /entity); antes disso o backend ignora phone_number
  const phoneEnabled = Boolean(signatureMethods)

  useEffect(() => {
    getSignnersList()
    }, [])

	const getSignnersList = useCallback(async () => {
	
        getSigners()
            .then((data) => {
              
              setUserList(data.signers)
            })
        
          }, [])

    const handleCreateSigner = () => {

      try {
        createSigner(signerName, signerEmail, signerDocumentNumber, signerPhone)
        .then((res) => {
            if (res?.error) {
              setSignerPhoneError(res.error.errors.find((e) => e.field === 'phone_number')?.message || '')
              return
            }
            setSignerName('')
            setSignerEmail('')
            setSignerDocumentNumber('')
            setSignerPhone('')
            setSignerPhoneError('')
            setShowAddSigner(false)
            getSignnersList()
        })
      } catch (error) {
        console.log(error);
      }
    }

    const handleDeleteteSigner = () => {
      createSigner(signerName, signerEmail, signerDocumentNumber)
      .then((res) => {
          setSignerName('')
          setSignerEmail('')
          setSignerDocumentNumber('')
          setShowAddSigner(false)
          getSignnersList()
      })
  }

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = userList.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleToggleAddUser = () => {
    setShowAddSigner(!showAddSigner)
  }

  const handleOpenDeleteDialog = (signer) => {
    setSignerToDelete(signer)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSignerToDelete(null)
  }

  const handleConfirmDelete = () => {
    if (signerToDelete) {
      DeleteSigner(signerToDelete.signer_key)
        .then((res) => {
          setPage(0)
          getSignnersList()
          handleCloseDeleteDialog()
        })
        .catch((error) => {
          console.error('Error deleting signer:', error)
          handleCloseDeleteDialog()
        })
    }
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - userList.length) : 0;

  const filteredUsers = applySortFilter(userList, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Assinantes </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Assinantes
          </Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleToggleAddUser}>
            Adicionar Assinante
          </Button>
        </Stack>

        { showAddSigner ? 
            <Stack justifyContent="space-between" mb={5}>
                <Card>
                    <SignerCard 
                        signerName={signerName} 
                        setSignerName={setSignerName}
                        signerDocumentNumber={signerDocumentNumber} 
                        setSignerDocumentNumber={setSignerDocumentNumber}
                        setSignerDocumentNumberError={setSignerDocumentNumberError}
                        signerDocumentNumberError={signerDocumentNumberError}
                        signerEmail={signerEmail}
                        setSignerEmail={setSignerEmail}
                        signerPhone={signerPhone}
                        setSignerPhone={phoneEnabled ? setSignerPhone : null}
                        signerPhoneError={signerPhoneError}
                        handleCreateSigner={handleCreateSigner}/>
                </Card>
            </Stack> : <></> }

        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={userList.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  showCheckbox={false}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell component="th" scope="row" padding="normal">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              {row.signer_name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{row.signer_email}</TableCell>

                        <TableCell align="left">{row.signer_document_number}</TableCell>

                        <TableCell align="left">{row.created_at.substring(0, 10).concat(' ').concat(row.created_at.substring(11, 19))}</TableCell>

                        <TableCell align="right">
                          {phoneEnabled && (
                            <IconButton size="large" aria-label="Editar celular" onClick={() => navigate(`/dashboard/signers/${row.signer_key}/edit`)}>
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          )}
                          <IconButton size="large" color="error" onClick={() => handleOpenDeleteDialog(row)}>
                            <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Não encontrado
                          </Typography>

                          <Typography variant="body2">
                            Nenhum resultado encontrado para &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Tente verificar erros de digitação ou usar palavras completas.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={userList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={_ => handleDeleteteSigner()}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir o assinante <strong>{signerToDelete?.signer_name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Sim, excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
